/**
 * EpisodeRail — Alvo D round2 (mockup 03/2a+2b): a trilha lateral de
 * episódios do fio contínuo.
 *
 * NASCE RECOLHIDA (decisão 3 da rodada): régua de 26px só com pontos — a
 * mesma língua de cor da trilha do grupo (verde resolvida · cinza arquivada ·
 * vermelho ativa; o episódio CORRENTE usa laranja «você está aqui»). Expande
 * para coluna com data compacta + assunto por item. Clique pula o fio ao
 * episódio (ou navega, se o cursor ainda não o carregou — via onJump).
 *
 * UX do toggle (feedback vivo do TL, 12-13/Ago — duas rodadas):
 * - O estado aberto/recolhido PERSISTE por operador (localStorage) — navegar
 *   a outro episódio remonta o componente e antes recolhia sozinho.
 * - Alvo de toggle = botão ▸/◂ (controle acessível) + o espaço LIVRE abaixo da
 *   lista. A 1ª rodada pôs o alvo no container INTEIRO e a 2ª mediu o preço:
 *   os 4px de `gap` entre as linhas togglavam, então errar a linha por um pixel
 *   recolhia a trilha em vez de abrir a conversa. Alvo grande não pode morar
 *   em cima do alvo pequeno.
 * - Data compacta (dd/mm) na linha; a completa + desfecho vivem no hover/aria.
 */
import { useState } from 'react';

import { episodeDotKind } from '../../inbox/EpisodeTrail';
import {
  conversationOutcomeLabel,
  conversationStatusLabel,
  formatShortDate,
} from '../../persons/persons-adapter';

import type { PersonConversation } from '../../persons/types';

/** Tipografia do rail — `text-2xs` não existe no tailwind.config (medido:
 * a classe não aplica nada e a fonte herdava o tamanho do contexto — feedback
 * vivo do TL). Fonte vem do token do DS, explícita. */
const RAIL_FONT = { fontSize: 'var(--text-2xs, 10px)', lineHeight: 1.3 } as const;

const RAIL_STORAGE_KEY = 'lume:fio-rail';

function readRailChoice(): boolean {
  try {
    return localStorage.getItem(RAIL_STORAGE_KEY) === 'open';
  } catch {
    return false;
  }
}

function persistRailChoice(open: boolean): void {
  try {
    localStorage.setItem(RAIL_STORAGE_KEY, open ? 'open' : 'closed');
  } catch {
    // storage indisponível — a escolha só não persiste.
  }
}

/** Data compacta da linha (dd/mm) — a completa fica no hover/aria. */
export function compactDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

/**
 * Rótulo curto de um episódio: «07/08 · troca de plano».
 *
 * Vive aqui e é EXPORTADO porque o mesmo rótulo passou a ser usado fora da
 * trilha — na oferta de consulta do banner do fio (decisão do TL, 13/Ago: «só a
 * data é simplório demais e fica confuso; traz o textinho junto»). Duas cópias
 * do mesmo rótulo divergiriam no primeiro ajuste de qualquer um dos lados.
 */
export function rotuloDoEpisodio(e: PersonConversation): string {
  const desfecho =
    e.outcomeType !== undefined && e.outcomeType !== null
      ? conversationOutcomeLabel(e.outcomeType)
      : conversationStatusLabel(e.status);
  return `${compactDate(e.createdAt)} · ${e.excerpt ?? desfecho}`;
}

export function EpisodeRail({
  episodes,
  totalCount,
  currentConversationId,
  visibleConversationId,
  onJump,
}: {
  /** Episódios na ordem do endpoint (mais recente primeiro). */
  episodes: PersonConversation[];
  totalCount?: number;
  /**
   * Conversa ABERTA (a da rota) — marca FORTE (fundo âmbar). É a que o
   * cabeçalho e o Resumo vivo descrevem e para onde o COMPOSER escreve.
   */
  currentConversationId?: string;
  /**
   * Conversa VISÍVEL no fio agora (segue a rolagem). Marca deliberadamente
   * DIFERENTE e mais fraca: rolar não pode dar a impressão de ter trocado a
   * conversa aberta, porque o composer continua escrevendo na aberta —
   * confundir as duas faria o operador responder dentro de uma conversa
   * arquivada sem perceber.
   */
  visibleConversationId?: string;
  onJump: (conversationId: string) => void;
}): JSX.Element {
  // Nasce recolhida (decisão 3); a escolha do operador persiste entre
  // conversas/remontes (feedback TL: recolhia sozinha ao navegar).
  const [open, setOpen] = useState(readRailChoice);
  const toggle = (): void => {
    setOpen((v) => {
      persistRailChoice(!v);
      return !v;
    });
  };
  // Cronológica de cima para baixo (mais antigo no topo, corrente embaixo),
  // ordenada pelo INÍCIO da conversa (`createdAt`) — o mesmo relógio com que o
  // fio posiciona o episódio. Ordenação EXPLÍCITA em vez de `reverse()` da ordem
  // do endpoint: a trilha é posicional e não pode depender de quem a alimenta
  // (o endpoint ordenava por `updatedAt` até 13/Ago, e o fio subia e descia).
  const ordered = episodes
    .slice()
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const total = totalCount ?? episodes.length;
  // m4 (F.5) — a página da ficha traz até 20; com mais, a truncagem se declara.
  const truncated = total > episodes.length;

  return (
    // O alvo de toggle é o BOTÃO ▸/◂ + o espaço LIVRE abaixo da lista (o
    // `<div>` filler no fim). A superfície inteira já foi alvo e o TL mediu o
    // efeito colateral: os 4px de `gap` ENTRE as linhas também togglavam, então
    // errar a linha por um pixel recolhia a trilha em vez de abrir a conversa.
    <div
      data-testid="episode-rail"
      data-open={open}
      className="flex flex-col border-r border-border-subtle"
      style={{ width: open ? 148 : 26, background: 'var(--bg-surface-2)', flex: 'none' }}
    >
      <button
        type="button"
        data-testid="episode-rail-toggle"
        onClick={toggle}
        className="flex items-center gap-1 px-1.5 py-2 text-text-secondary hover:text-text-primary"
        style={RAIL_FONT}
        aria-expanded={open}
        aria-label={
          open ? 'Recolher trilha de episódios' : `Expandir trilha de episódios (${total})`
        }
      >
        <span aria-hidden>{open ? '◂' : '▸'}</span>
        {open && <span className="font-mono uppercase tracking-wide">Episódios · {total}</span>}
      </button>
      <div className="flex-1 overflow-auto flex flex-col items-stretch gap-1 py-1">
        {truncated && (
          <span
            className="px-1.5 text-text-secondary"
            style={RAIL_FONT}
            title={`${total - episodes.length} episódios mais antigos — ver no Histórico do cliente`}
            aria-hidden
          >
            ···
          </span>
        )}
        {ordered.map((e) => {
          const isCurrent = e.id === currentConversationId;
          // Visível ≠ aberta: só marca quando NÃO é a aberta (senão as duas
          // marcas brigariam pela mesma linha e a mais fraca venceria).
          const isVisible = !isCurrent && e.id === visibleConversationId;
          const kind = isCurrent ? 'current' : episodeDotKind(e);
          const label =
            e.outcomeType !== undefined && e.outcomeType !== null
              ? conversationOutcomeLabel(e.outcomeType)
              : conversationStatusLabel(e.status);
          // O texto da linha é data COMPACTA + ASSUNTO (excerpt do
          // LivingSummary); desfecho e data completa ficam no title/aria.
          const subject = e.excerpt ?? label;
          // (o rótulo curto vive em `rotuloDoEpisodio` — a trilha o compõe com
          // as mesmas peças; a oferta de consulta reusa a função.)
          // Data do INÍCIO da conversa: responde «quando isto aconteceu», e casa
          // com a posição do episódio no fio. `updatedAt` responderia «quando foi
          // tocado por último», que arquivamento e reclassificação movem.
          const rowText = `${compactDate(e.createdAt)} · ${subject}`;
          const title = `${formatShortDate(e.createdAt)} · ${label}${
            e.excerpt !== undefined && e.excerpt !== null ? ` · ${e.excerpt}` : ''
          }`;
          return (
            <button
              key={e.id}
              type="button"
              onClick={() => {
                onJump(e.id);
              }}
              title={title}
              aria-label={`Episódio ${title}${isCurrent ? ' (aberta)' : ''}${
                isVisible ? ' (visível no fio)' : ''
              }`}
              aria-current={isCurrent ? 'true' : undefined}
              data-visible={isVisible ? 'true' : undefined}
              className={`flex items-center gap-2 px-1.5 py-1 hover:bg-bg-canvas ${
                isCurrent ? 'font-semibold text-text-primary' : 'text-text-secondary'
              }`}
              style={{
                ...RAIL_FONT,
                // Episódio ABERTO: fundo âmbar + barra em `--brand`. A barra
                // era `--warning-border` (#FCD34D) sobre `--warning-bg`
                // (#FEF3C7) — medido no browser em 13/Ago: invisível, porque
                // são quase a mesma cor. `--brand` (#D97706) se vê.
                ...(isCurrent && {
                  background: 'var(--warning-bg)',
                  boxShadow: 'inset 3px 0 0 0 var(--brand)',
                }),
                // Marca da VISÍVEL («estou lendo aqui»): fundo NEUTRO próprio +
                // barra cinza média. Antes era só a barrinha, sem fundo algum
                // (medido: `rgba(0,0,0,0)`), e o TL leu como defeito — linha
                // marcada sem fundo parece bug, não escolha.
                //
                // Continua DELIBERADAMENTE diferente da aberta, e é a distinção
                // que importa: o composer escreve na ABERTA. Igualar as marcas
                // faria o operador responder dentro de um episódio arquivado
                // sem perceber. Guard em `EpisodeRail.spec.tsx`.
                // A barra é `--fg-3`, não `--fg-4`: medido contra o fundo da
                // trilha (`#F5F5F4`), `--fg-4` dava contraste 2.31 e o fundo
                // `--bg-muted` dá 1.15 — gray-on-gray some. `--fg-3` dá 4.40,
                // então a linha se lê como MARCADA mesmo com o fundo discreto.
                // A distinção contra a aberta é de MATIZ (cinza = estou lendo ·
                // âmbar = estou trabalhando), não de intensidade.
                ...(isVisible && {
                  background: 'var(--bg-muted)',
                  boxShadow: 'inset 3px 0 0 0 var(--fg-3)',
                }),
              }}
            >
              <span
                aria-hidden
                className="episode-rail__dot"
                style={{
                  width: isCurrent ? 9 : 7,
                  height: isCurrent ? 9 : 7,
                  borderRadius: 999,
                  flex: 'none',
                  // Tokens REAIS do DS (Item 437). Os nomes da escala `-500` e
                  // `neutral-300` nunca existiram no design system — vinham de
                  // `design-references/mensageria-condensada/colors_and_type.css`,
                  // paleta própria de um handoff do Claude Design que não foi
                  // adotada. O mapeamento não é chute: aquele mesmo arquivo
                  // declara os apelidos (brand ← accent-500 · border-strong ←
                  // neutral-300).
                  background:
                    kind === 'current'
                      ? 'var(--brand)'
                      : kind === 'active'
                        ? 'var(--danger-fg)'
                        : kind === 'done'
                          ? 'var(--success-fg)'
                          : 'var(--border-strong)',
                }}
              />
              {open && <span className="truncate">{rowText}</span>}
            </button>
          );
        })}
        {/* Espaço LIVRE abaixo da lista — alvo grande de toggle, longe das
            linhas. Quando a lista enche a coluna ele colapsa a zero e só o
            botão ▸/◂ toggla; é o preço honesto de não roubar clique de linha. */}
        <button
          type="button"
          data-testid="episode-rail-filler"
          onClick={toggle}
          className="flex-1 cursor-pointer"
          tabIndex={-1}
          aria-hidden
          title={open ? 'Recolher trilha' : `Episódios · ${total} — expandir`}
        />
      </div>
    </div>
  );
}
